<template>
	<div class="creatives">
		<div class="generate">
			<div class="form-group">
				<label>{{ $t('copilot.elements.content.editor.creativeModal.prompt.label') }}</label>
				<textarea v-model="prompt"
						  class="form-control"
						  :placeholder="$t('copilot.elements.content.editor.creativeModal.prompt.placeholder')"
				/>
			</div>

			<div class="form-group">
				<label>{{ $t('copilot.elements.content.editor.creativeModal.style.label') }}</label>
				<div class="styles">
					<div v-for="item in styles"
						 @click="style = item"
						 :class="{ active: style === item }"
						 class="style"
						 :key="item">
						{{ $t(`copilot.elements.content.editor.creativeModal.style.${item}`) }}
					</div>
				</div>
			</div>

			<div class="form-group">
				<label>{{ $t('copilot.elements.content.editor.creativeModal.aspectRatio.label') }}</label>
				<div class="aspect-ratios">
					<div @click="aspectRatio = '1:1'"
						 :class="{ active: aspectRatio === '1:1' }"
						 class="aspect-ratio aspect-1-1">
						1:1
						<small>1024x1024</small>
					</div>

					<div @click="aspectRatio = '16:9'"
						 :class="{ active: aspectRatio === '16:9' }"
						 class="aspect-ratio aspect-16-9">
						16:9
						<small>1792x1024</small>
					</div>

					<div @click="aspectRatio = '9:16'"
						 :class="{ active: aspectRatio === '9:16' }"
						 class="aspect-ratio aspect-9-16">
						9:16
						<small>1024x1792</small>
					</div>
				</div>
			</div>

			<button type="button"
					class="btn btn-dark py-3 px-4"
					:disabled="!prompt.trim()"
					@click="generate(prompt, style)">
				{{ $t('copilot.elements.content.editor.creativeModal.prompt.button') }}
			</button>
		</div>

		<!-- Currently generating image (if any) -->
		<div v-if="generatingCreative" class="generating-creative">
			<h4 class="section-title">Genererer billede</h4>
			<div class="generation-status">
				<i class="fa fa-spinner fa-spin fa-fw"></i>
				<span>{{ generatingCreative.percentage || 0 }}%</span>
			</div>
			<div class="progress-container">
				<div class="progress-bar" :style="{ width: `${generatingCreative.percentage || 0}%` }"></div>
			</div>
			<div class="prompt-preview">{{ generatingCreative.prompt }}</div>
		</div>

		<!-- Simple existing images section -->
		<div class="existing-images-section">
			<h4 class="section-title">Eksisterende billeder</h4>

			<!-- Empty state that matches screenshot -->
			<div v-if="filteredCreatives.length === 0" class="empty-state">
				<div class="empty-icon">
					<img src="/images/placeholder-image.svg" alt="No images" @error="showFallbackIcon = true" v-if="!showFallbackIcon" />
					<i v-else class="fa fa-image"></i>
				</div>
				<p class="empty-text">Ingen billeder genereret endnu</p>
			</div>

			<!-- Image list -->
			<div v-else class="images-list">
				<dalle-creative v-for="creative in filteredCreatives"
								:key="`creative-${creative.id}`"
								:creative="creative"
								@add="$path => onAdd($path)"
								@remove="onRemove(creative.id)"
				/>

				<!-- Pagination -->
				<div v-if="creatives.total > creatives.limit" class="pagination-wrapper">
					<pagination :total="creatives.total"
								:page="creatives.page"
								:limit="creatives.limit"
								v-on:paginate="paginate"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.generate {
	margin-bottom: 15px;

	button {
		color: #fff;
		letter-spacing: -.1px;
		font-weight: 500;
		opacity: 1 !important;
		background-color: #000 !important;
		border: 0;
		font-size: 14px;
		padding: 14px 25px !important;
	}
}

.creatives {
	display: flex;
	flex-direction: column;
	gap: 6px;

	.dalle-creative {
		display: flex;
		flex-direction: column;
		gap: 10px;
		padding: 15px 0;
		border-top: 1px solid #eee;

		&:first-child {
			border-top: 0;
		}

		.top {
			display: flex;
			gap: 10px;

			.intro {
				display: flex;
				flex-direction: column;
				gap: 10px;
				flex: 1;

				.style {
					display: inline-block;
					color: #282828;
					padding: 4px 6px;
					border-radius: 4px;
					font-size: 11px;
					font-weight: 500;
					background-color: #eee;
				}

				.prompt {
					color: #343434;
					font-size: 13px;
					font-style: italic;
					border-radius: 8px;
					padding: 6px 10px;
					border: 2px solid #eee;
				}
			}
		}

		.results {
			.loader {
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: center;
				gap: 10px;
				padding: 20px;
				color: #666;
				font-size: 14px;
				text-align: center;

				i {
					font-size: 24px;
				}
			}

			.images {
				display: grid;
				grid-template-columns: repeat(4, 1fr);
				gap: 6px;

				.placeholder {
					aspect-ratio: 1;

					> div {
						height: 100%;
						border-radius: 8px;
						background-color: #eee;
						position: relative;
						overflow: hidden;
						animation: blink 1s infinite;
					}
				}
			}
		}
	}
}

@keyframes blink {
	0% { opacity: 1; }
	50% { opacity: 0.5; }
	100% { opacity: 1; }
}

.pagination-wrapper {
	display: flex;
	justify-content: center;
}

.aspect-ratios {
	display: flex;
	gap: 1rem;
	margin-top: 0.5rem;

	.aspect-ratio {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 1rem;
		border: 1px solid #ccc;
		border-radius: 4px;
		cursor: pointer;
		transition: all 0.2s;

		small {
			margin-top: 0.25rem;
			color: #666;
		}

		&:hover {
			background: #f5f5f5;
		}

		&.active {
			background: #000;
			color: white;
			border-color: #000;

			small {
				color: #ccc;
			}
		}
	}
}

.styles {
	display: flex;
	align-items: center;
	gap: 6px;
	flex-wrap: wrap;

	> .style {
		cursor: pointer;
		color: #7d7979;
		line-height: 100%;
		padding: 10px 14px;
		border-radius: 32px;
		border: 1px solid #eee;
		font-size: 13px;
		font-weight: 400;
		white-space: nowrap;

		&:hover {
			color: #000;
			border-color: #000;
		}

		&.active {
			color: #000;
			box-shadow: inset 0 0 0px 1px #000;
			border-color: #000;
		}
	}
}

.section-title {
	font-size: 16px;
	font-weight: 500;
	margin: 20px 0 10px 0;
	padding-bottom: 8px;
	border-bottom: 1px solid #eee;
}

.generating-creative {
	margin: 20px 0;
	padding: 15px;
	border: 1px solid #eee;
	border-radius: 8px;
	background-color: #f9f9f9;

	.generation-status {
		display: flex;
		align-items: center;
		gap: 10px;
		margin-bottom: 10px;
		font-size: 14px;

		i {
			color: #007bff;
		}
	}

	.progress-container {
		height: 6px;
		background-color: #e9ecef;
		border-radius: 3px;
		margin-bottom: 10px;
		overflow: hidden;

		.progress-bar {
			height: 100%;
			background-color: #007bff;
			transition: width 0.3s ease;
		}
	}

	.prompt-preview {
		font-size: 13px;
		font-style: italic;
		color: #666;
	}
}

.existing-images-section {
	margin-top: 25px;
	padding-top: 15px;
	border-top: 1px solid #eee;

	.empty-state {
		text-align: center;
		padding: 40px 0;
		background-color: #f9f9f9;
		border-radius: 5px;

		.empty-icon {
			margin-bottom: 10px;

			img {
				width: 48px;
				height: 48px;
				opacity: 0.5;
			}
		}

		.empty-text {
			margin: 0;
			color: #999;
			font-size: 14px;
		}
	}

	.images-list {
		margin-top: 15px;

		.dalle-creative {
			margin-bottom: 15px;
			padding-bottom: 15px;
			border-bottom: 1px solid #eee;

			&:last-child {
				border-bottom: none;
			}
		}
	}
}
</style>

<script>
import DALLECreative from './DALLECreative.vue'
import Pagination from '@/app/layout/components/paginator/Pagination'
import DALLEService from '@/services/ai/DALLEService'

export default {
	name: 'creatives',
	props: {
		customerId: {
			type: String,
			required: true
		},
		generating: {
			type: Boolean,
			required: true
		},
		creatives: {
			type: Object,
			required: true,
			validator: function(obj) {
				return ['items', 'total', 'page', 'limit'].every(key => key in obj)
			}
		}
	},

	computed: {
		filteredCreatives() {
			// Filter out any creatives that are currently being generated
			// so they don't appear in both sections simultaneously
			if (!this.generatingCreative) {
				return this.creatives.items;
			}

			return this.creatives.items.filter(creative =>
				creative.id !== this.generatingCreative.id
			);
		}
	},

	data() {
		return {
			prompt: '',
			style: 'natural',
			interval: null,
			generatingCreative: null,
			completedCreative: null,
			aspectRatio: '1:1',
			showFallbackIcon: false,
			styles: [
				'photorealistic',
				'cartoon',
				'pixel_art',
				'drawing',
			]
		}
	},

	watch: {
		generating(newVal) {
			if (newVal) {
				this.startPolling()
			} else {
				this.stopPolling()
			}
		},

		customerId() {
			this.$emit('update')
		},

		'creatives.items': {
			handler(newItems) {
				// console.log('Images updated:', newItems.length, 'images available');
			},
			deep: true
		}
	},

	beforeDestroy() {
		this.stopPolling()
	},

	mounted() {
		// Load existing images when component mounts
		this.loadExistingImages();

		// Check for any ongoing generations
		this.checkForInProgressGeneration();
	},

	methods: {
		loadExistingImages() {
			// Directly load existing images from DALLEService
			DALLEService.findAll({
				sort: [{
					key: 'created',
					direction: 'DESC'
				}],
				page: this.creatives.page,
				limit: this.creatives.limit,
				filter_groups: [{
					filters: [{
						key: 'customer',
						operator: 'eq',
						value: this.customerId
					}]
				}]
			}, (response) => {
				// Check if we have data in the response
				if (response && response.data && response.data.rows) {
					// Emit update with the loaded data
					this.$emit('update', {
						items: response.data.rows,
						total: response.data.total || 0,
						page: response.data.page || this.creatives.page,
						limit: response.data.limit || this.creatives.limit
					});
				} else {
					// Fallback to just triggering the standard update
					this.$emit('update');
				}
			}, () => {
				// On error, still try the standard update
				this.$emit('update');
			});
		},

		checkForInProgressGeneration() {
			// Check localStorage for any ongoing generation
			const generatingId = localStorage.getItem('generatingCreativeId');
			if (generatingId) {
				// Set generating state
				this.generating = true;
				this.$emit('generating', true);

				// Fetch the latest info for this creative
				DALLEService.find(generatingId, (response) => {
					if (response.data) {
						const data = response.data;

						// Calculate percentage based on image results
						const imageResults = data.dalleImageResults || [];
						const completedCount = imageResults.filter(img => img && img.path).length;
						const percentage = completedCount * 25;

						// Update the creative with latest data
						this.generatingCreative = {
							...data,
							percentage,
							status: percentage >= 100 ? 'completed' : data.status
						};

						// If it's complete, clear localStorage
						if (percentage >= 100 || data.status === 'completed' || data.status === 'failed') {
							localStorage.removeItem('generatingCreativeId');
							// Also clear generatingCreative to hide the UI
							this.generatingCreative = null;
							this.generating = false;
							this.$emit('generating', false);
							// Reload to get the completed image
							this.load();
						} else {
							// Otherwise start polling for updates
							this.startPolling();
						}
					} else {
						// If the creative no longer exists, clear localStorage
						localStorage.removeItem('generatingCreativeId');
						this.generating = false;
						this.$emit('generating', false);
					}
				});
			}
		},

		stopPolling() {
			if (this.interval) {
				clearInterval(this.interval)
				this.interval = null
			}
		},

		startPolling() {
			this.stopPolling()

			this.interval = setInterval(() => {
				if (!this.generatingCreative || !this.generatingCreative.id) {
					this.stopPolling()
					return
				}

				DALLEService.find(this.generatingCreative.id, (response) => {
					if (response.data) {
						const latest = response.data

						// Update the creative with latest data
						const imageResults = latest.dalleImageResults || []

						// Calculate percentage based on number of images with paths (25% each)
						const completedCount = imageResults.filter(img => img && img.path).length
						const percentage = completedCount * 25

						// Update the creative with latest data
						this.generatingCreative = {
							...latest,
							dalleImageResults: imageResults,
							percentage,
							status: percentage >= 100 ? 'completed' : latest.status
						}

						// When complete, clear the generating creative state
						if (percentage >= 100 || latest.status === 'completed' || latest.status === 'failed') {
							// Remove from localStorage to prevent reloading on page refresh
							localStorage.removeItem('generatingCreativeId');

							// Clear the generating creative to hide the UI
							this.generatingCreative = null;

							this.$emit('generating', false)
							this.$emit('update')
							this.stopPolling()

							// Load the latest creatives to include this one
							this.load()
						}
					}
				})
			}, 1000)
		},

		generate(prompt, style) {
			this.generatingCreative = {
				id: null,
				prompt,
				style,
				status: 'pending',
				percentage: 0,
				dalleImageResults: Array(4).fill({})
			}

			this.$emit('generating', true)

			DALLEService.generate({
				prompt,
				style,
				aspectRatio: this.aspectRatio,
				customerId: this.customerId
			}, (response) => {
				if (response.data && response.data.id) {
					// Store ID in localStorage for persistence
					localStorage.setItem('generatingCreativeId', response.data.id);

					this.generatingCreative = {
						...this.generatingCreative,
						id: response.data.id,
						dalleImageResults: response.data.dalleImageResults || Array(4).fill({})
					}
					this.startPolling()
				}
			})
		},

		load() {
			// Call our direct method instead of just emitting
			this.loadExistingImages();
		},

		onAdd(path) {
			this.$emit('add', path)
		},

		onRemove(id) {
			// Don't remove if it's the currently generating creative
			if (this.generatingCreative && this.generatingCreative.id === id) {
				return
			}
			DALLEService.remove(id, () => {
				this.$emit('update')
			})
		},

		paginate(page) {
			this.$emit('paginate', page)
		}
	},

	components: {
		'dalle-creative': DALLECreative,
		Pagination
	}
}
</script>
